import * as React from "react";

export interface CustomerSessionConsumerInterface {
  isCustomerLoggedIn: boolean;
}
const defaultValue = {} as unknown as CustomerSessionConsumerInterface;
const CustomerSessionContext = React.createContext(defaultValue);

export interface CustomerSession_ControllerConsumerInterface {
  handleCustomerLoggedIn: () => void;
}
const defaultValue_controller =
  {} as unknown as CustomerSession_ControllerConsumerInterface;
const CustomerSession_ControllerContext = React.createContext(
  defaultValue_controller
);

export interface InitialStateMinimumRequirements_CustomerSession {
  isCustomerLoggedIn?: boolean;
}

export interface CustomerSessionProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_CustomerSession;
  };
}
export const CustomerSessionProvider: React.FunctionComponent<
  CustomerSessionProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [isCustomerLoggedIn, setIsCustomerLoggedIn] = React.useState(() => {
    const { isCustomerLoggedIn: wasTheCustomerLoggedInInitially } =
      initialState || {};
    return wasTheCustomerLoggedInInitially || false;
  });

  const handleCustomerLoggedIn: CustomerSession_ControllerConsumerInterface["handleCustomerLoggedIn"] =
    React.useCallback(() => {
      setIsCustomerLoggedIn(true);
    }, [setIsCustomerLoggedIn]);

  const value: CustomerSessionConsumerInterface = React.useMemo(
    () => ({
      isCustomerLoggedIn,
    }),
    [isCustomerLoggedIn]
  );

  const controllerValue: CustomerSession_ControllerConsumerInterface =
    React.useMemo(
      () => ({
        handleCustomerLoggedIn,
      }),
      [handleCustomerLoggedIn]
    );

  return (
    <CustomerSessionContext.Provider value={value}>
      <CustomerSession_ControllerContext.Provider value={controllerValue}>
        {props.children || []}
      </CustomerSession_ControllerContext.Provider>
    </CustomerSessionContext.Provider>
  );
};

export const useCustomerSession = () => {
  const value = React.useContext(CustomerSessionContext);
  if (value === defaultValue) {
    throw new Error(
      "useCustomerSession must be used within a CustomerSessionProvider"
    );
  }
  return value;
};

export const useCustomerSession_asController = () => {
  const value = React.useContext(CustomerSession_ControllerContext);
  if (value === defaultValue_controller) {
    throw new Error(
      "useCustomerSession_asController must be used within a CustomerSessionProvider"
    );
  }
  return value;
};
